import { useState, useEffect, useCallback, useRef } from 'react';
import { useCopy, fill } from '../../i18n';
import { Joyride, STATUS, EVENTS } from 'react-joyride';
import type { EventData, Step, Controls } from 'react-joyride';
import { useProject } from '../../contexts/ProjectContext';
import { useUIState } from '../../contexts/UIStateContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { flattenTasks } from '../../utils/taskUtils';
import type { Task } from '../../types/database';
import analytics from '../../services/analyticsService';

const ONBOARDING_KEY = 'meugantt_onboarding_completed';
const LAST_STEP_KEY = 'meugantt_onboarding_v2_last_step';

// Chave estável por passo, usada para telemetria e para o checklist (Fase 2).
const STEP_KEYS = [
  'edit_task',
  'set_dates',
  'add_subtask',
  'add_dependency',
  'assign_owner',
  'kanban_view',
  'save_baseline',
  'premium_teaser',
] as const;

function toISO(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

interface Fingerprint {
  name: string;
  start: string;
  end: string;
  assigned: string;
}

function fingerprint(tasks: Task[]): Map<string, Fingerprint> {
  const map = new Map<string, Fingerprint>();
  for (const t of flattenTasks(tasks)) {
    map.set(t.id, {
      name: t.name || '',
      start: toISO(t.startDate),
      end: toISO(t.endDate),
      assigned: t.assigned || '',
    });
  }
  return map;
}


interface OnboardingTourProps {
  forceRun?: boolean;
  onComplete?: () => void;
}

/**
 * Tour de ativação acionável. Roda sobre um projeto já populado (semeado por modelo no
 * primeiro acesso) e percorre o sistema ponta a ponta: editar tarefa, prazos, subtarefa,
 * dependência/caminho crítico, responsável, Kanban, linha de base e teaser do Professional.
 *
 * Navegação por "Próximo" (nunca trava), com o elemento real clicável no spotlight
 * (blockTargetInteraction: false). Em paralelo, detecta os marcos de ativação por sinal de
 * estado: M1 (editou uma tarefa) e M2 (atribuiu um responsável), disparando os eventos de
 * analytics que medem o funil de ativação.
 */
export default function OnboardingTour({ forceRun = false, onComplete }: OnboardingTourProps) {
  const t = useCopy().onboarding.tour;
  const [run, setRun] = useState(false);
  const { tasks } = useProject();
  const { currentModule } = useUIState();
  const { hasFeature, isSystemAdmin } = useSubscription();
  const canUseBaseline = isSystemAdmin || hasFeature('schedule_baseline');
  const canUseCriticalPath = isSystemAdmin || hasFeature('critical_path');

  const startedRef = useRef(false);
  const baselineRef = useRef<Map<string, Fingerprint> | null>(null);
  const m1FiredRef = useRef(false);
  const m2FiredRef = useRef(false);
  const completeFiredRef = useRef(false);

  const isCompleted = useCallback(() => {
    try {
      return localStorage.getItem(ONBOARDING_KEY) === 'true';
    } catch {
      return false;
    }
  }, []);

  // Passos. O caminho de baseline só entra quando o recurso está liberado (trial/Professional),
  // para não apontar para um alvo inexistente no plano Free.
  const steps: Step[] = [
    {
      target: '[data-testid="task-name-cell"]',
      title: t.steps.editTask.title,
      content: t.steps.editTask.body,
      placement: 'bottom',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    {
      target: '[data-tour="gantt-chart"]',
      title: t.steps.setDates.title,
      content: t.steps.setDates.body,
      placement: 'left',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    {
      target: '[data-tour="add-task"]',
      title: t.steps.addSubtask.title,
      content: t.steps.addSubtask.body,
      placement: 'top',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    {
      // Ancorado no botão Caminho Crítico (sempre visível no topo) quando liberado;
      // senão, no próprio gráfico, para o balão nunca cair fora da tela.
      target: canUseCriticalPath ? '[data-tour="critical-path-toggle"]' : '[data-tour="gantt-chart"]',
      title: t.steps.addDependency.title,
      content: t.steps.addDependency.body,
      placement: 'bottom',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    {
      target: '[data-tour="responsavel-filter"]',
      title: t.steps.assignOwner.title,
      content: t.steps.assignOwner.body,
      placement: 'bottom',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    {
      target: '[data-tour="module-tabs"]',
      title: t.steps.kanbanView.title,
      content: t.steps.kanbanView.body,
      placement: 'bottom',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
    ...(canUseBaseline
      ? [{
          target: '[data-tour="baseline-save"]',
          title: t.steps.saveBaseline.title,
          content: t.steps.saveBaseline.body,
          placement: 'bottom' as const,
          skipBeacon: true,
          blockTargetInteraction: false,
        }]
      : []),
    {
      target: '[data-tour="premium-teaser"]',
      title: t.steps.premiumTeaser.title,
      content: (
        <div className="space-y-2">
          <p>{t.steps.premiumTeaser.body}</p>
          <button
            type="button"
            data-testid="premium-teaser-cta"
            onClick={() => window.dispatchEvent(new CustomEvent('meugantt:open-pricing', { detail: { source: 'onboarding' } }))}
            className="text-[13px] font-semibold text-[var(--color-primary)] underline underline-offset-2"
          >
            {t.steps.premiumTeaser.cta}
          </button>
        </div>
      ),
      placement: 'right',
      skipBeacon: true,
      blockTargetInteraction: false,
    },
  ];

  // Decidir quando rodar: forceRun, query (?forceOnboarding=1) / window.__startOnboarding,
  // ou auto no primeiro projeto populado, se ainda não concluído.
  useEffect(() => {
    if (forceRun) {
      startedRef.current = false;
      setRun(true);
    }
  }, [forceRun]);

  useEffect(() => {
    // Gatilho de QA/retomar.
    const start = () => { startedRef.current = false; setRun(true); };
    (window as unknown as { __startOnboarding?: () => void }).__startOnboarding = start;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('forceOnboarding') === '1') start();
    } catch { /* ignore */ }
    return () => {
      delete (window as unknown as { __startOnboarding?: () => void }).__startOnboarding;
    };
  }, []);

  useEffect(() => {
    if (run || forceRun) return;
    if (isCompleted()) return;
    if (tasks.length === 0) return; // espera o projeto semeado popular
    // Pequeno respiro para o Gantt assentar antes de ancorar o tour. Os dados já
    // estão carregados nesse ponto, então não precisa ser longo.
    const timer = setTimeout(() => setRun(true), 500);
    return () => clearTimeout(timer);
  }, [tasks.length, run, forceRun, isCompleted]);

  // Snapshot de referência ao iniciar, para detectar edições do usuário.
  useEffect(() => {
    if (run && !baselineRef.current) {
      baselineRef.current = fingerprint(tasks);
      if (!startedRef.current) {
        startedRef.current = true;
        analytics.trackOnboardingStarted(forceRun ? 'manual' : 'auto');
      }
    }
    if (!run) {
      baselineRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  // Detecção dos marcos de ativação (M1 editou tarefa, M2 atribuiu responsável).
  useEffect(() => {
    const base = baselineRef.current;
    if (!run || !base) return;
    const current = fingerprint(tasks);

    let edited = false;
    let assigned = false;
    for (const [id, cur] of current) {
      const prev = base.get(id);
      if (!prev) continue;
      if (cur.name !== prev.name || cur.start !== prev.start || cur.end !== prev.end) edited = true;
      if (cur.assigned !== prev.assigned) assigned = true;
    }

    if (edited && !m1FiredRef.current) {
      m1FiredRef.current = true;
      analytics.trackActivationM1Edit();
    }
    if (assigned && !m2FiredRef.current) {
      m2FiredRef.current = true;
      analytics.trackActivationM2Assign();
    }
    if (m1FiredRef.current && m2FiredRef.current && !completeFiredRef.current) {
      completeFiredRef.current = true;
      analytics.trackActivationComplete();
    }
  }, [tasks, run]);

  const finish = useCallback(() => {
    setRun(false);
    try { localStorage.setItem(ONBOARDING_KEY, 'true'); } catch { /* ignore */ }
    onComplete?.();
  }, [onComplete]);

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { status, type, index } = data;

    if (type === EVENTS.STEP_AFTER && typeof index === 'number') {
      const stepKey = STEP_KEYS[index] || `step_${index}`;
      analytics.trackOnboardingStep(index, stepKey);
      try { localStorage.setItem(LAST_STEP_KEY, String(index)); } catch { /* ignore */ }
    }

    if (status === STATUS.FINISHED) {
      analytics.trackOnboardingFinished();
      finish();
    } else if (status === STATUS.SKIPPED) {
      analytics.trackOnboardingSkipped(typeof index === 'number' ? index : -1);
      finish();
    }
  }, [finish]);

  if (!run) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={t}
      options={{
        primaryColor: '#7e3eee',
        backgroundColor: '#ffffff',
        textColor: '#1e293b',
        arrowColor: '#ffffff',
        overlayColor: 'rgba(15, 18, 25, 0.55)',
        zIndex: 10000,
        showProgress: true,
        buttons: ['back', 'primary', 'skip'],
      }}
      styles={{
        tooltip: {
          borderRadius: '12px',
          padding: '24px',
          fontSize: '14px',
          boxShadow: '0 20px 40px rgba(126, 62, 238, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(126, 62, 238, 0.12)',
        },
        tooltipContainer: { textAlign: 'left' },
        buttonPrimary: {
          backgroundColor: '#7e3eee',
          borderRadius: '8px',
          padding: '10px 20px',
          fontSize: '13px',
          fontWeight: 600,
        },
        buttonBack: { color: '#64748b', fontSize: '13px', fontWeight: 500, marginRight: '8px' },
        buttonSkip: { color: '#94a3b8', fontSize: '13px' },
        buttonClose: { color: '#94a3b8' },
        overlay: { mixBlendMode: 'unset' as const },
      }}
    />
  );
}

export function resetOnboarding() {
  try {
    localStorage.removeItem(ONBOARDING_KEY);
    localStorage.removeItem(LAST_STEP_KEY);
  } catch { /* ignore */ }
}

export function isOnboardingCompleted(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'true';
  } catch {
    return false;
  }
}
